import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person, PersonType } from '@core/data-access/models/person.model';
import { SavedTicketHolders } from '@core/features/account/features/saved-ticket-holders/models/saved-ticket-holders.store.model';
import {
  TicketAssignmentStoreState,
  TicketHolderQuestionnaireDynamicFields,
  TicketHolderQuestionnaireForms
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import { removeNonQuestionnaireFormFields } from '@core/utils/form-fields/form-field-helpers';
import { TariffUuids } from '@products/models/tariff.model';
import { isEmpty, isNil, omit, reduce } from 'lodash';

export const addTicketHolderQuestionnaireFormOnTicketHolderAssign = (
  state: TicketAssignmentStoreState,
  person: Person,
  formFields: FormFields
) => ({
  ...state,
  forms: {
    ...state.forms,
    [FormName.TicketHolderQuestionnaire]: {
      ...state.forms[FormName.TicketHolderQuestionnaire],
      [state.selectedTariffUuid]: {
        person,
        formFields: removeNonQuestionnaireFormFields(formFields)
      }
    }
  }
});

export const removeTicketHolderQuestionnaireFormOnTicketHolderAssign = (state: TicketAssignmentStoreState) => ({
  ...state,
  forms: {
    ...state.forms,
    [FormName.TicketHolderQuestionnaire]: omit(
      state.forms[FormName.TicketHolderQuestionnaire],
      state.selectedTariffUuid
    )
  }
});

export const addTicketHolderQuestionnaireFormsOnSendingOptionChange = (
  state: TicketAssignmentStoreState,
  tariffUuids: TariffUuids,
  savedTicketHolders: SavedTicketHolders
) => {
  const { ticketHolders, forms } = state;
  const ticketHolderForms = forms[FormName.TicketHolder];

  if (isEmpty(ticketHolderForms)) {
    return state;
  }

  const updatedTicketHolderQuestionnaireForms: Partial<TicketHolderQuestionnaireForms> = reduce(
    tariffUuids,
    (updatedTicketHolderQuestionnaireForms, tariffUuid) => {
      const ticketHolderForm = ticketHolderForms[tariffUuid];
      const isUnassignedTicket = isNil(ticketHolderForm?.person);

      if (isUnassignedTicket) {
        return updatedTicketHolderQuestionnaireForms;
      }

      const { person } = ticketHolderForm;
      const { uuid, type } = person;

      const isBuyer = type === PersonType.BuyerIsHolder;

      if (!isBuyer) {
        return updatedTicketHolderQuestionnaireForms;
      }

      const { formFields } = ticketHolders[uuid] || savedTicketHolders[uuid];

      updatedTicketHolderQuestionnaireForms[tariffUuid] = {
        person,
        formFields: removeNonQuestionnaireFormFields(formFields)
      };

      return updatedTicketHolderQuestionnaireForms;
    },
    {}
  );

  return {
    ...state,
    forms: {
      ...forms,
      [FormName.TicketHolderQuestionnaire]: {
        ...forms[FormName.TicketHolderQuestionnaire],
        ...updatedTicketHolderQuestionnaireForms
      }
    }
  };
};

export const removeTicketHolderQuestionnaireFormsOnSendingOptionChange = (
  state: TicketAssignmentStoreState,
  tariffUuids: TariffUuids
) => {
  const { forms } = state;

  if (isEmpty(forms[FormName.TicketHolderQuestionnaire])) {
    return state;
  }

  return {
    ...state,
    forms: {
      ...forms,
      [FormName.TicketHolderQuestionnaire]: omit(forms[FormName.TicketHolderQuestionnaire], tariffUuids)
    }
  };
};

export const setTicketHolderQuestionnaireDynamicFields = (
  state: TicketAssignmentStoreState,
  ticketHolderQuestionnaireDynamicFields: TicketHolderQuestionnaireDynamicFields
) => {
  if (isEmpty(ticketHolderQuestionnaireDynamicFields)) {
    return state;
  }

  return {
    ...state,
    dynamicFields: {
      ...state.dynamicFields,
      [FormName.TicketHolderQuestionnaire]: {
        ...state.dynamicFields.ticketHolderQuestionnaire,
        ...ticketHolderQuestionnaireDynamicFields
      }
    }
  };
};

export const resetTicketHolderQuestionnaireDynamicFields = (state: TicketAssignmentStoreState) => ({
  ...state,
  dynamicFields: {
    ...state.dynamicFields,
    [FormName.TicketHolderQuestionnaire]: {}
  }
});
